import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  ButtonGroup,
  VStack,
  HStack,
  Box,
  Text,
  Input,
  Image,
  Select,
  Checkbox,
  FormControl,
  FormLabel,
  Divider,
  Spacer,
  useColorModeValue,
  useToast,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileCsv,
  faFilePdf,
  faFilePowerpoint,
  faRotateLeft,
  faUpload,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { apiClient, analyticsApi } from '../../api';
import { parseDateRange } from '../../hooks/useAnalytics';
import { ColorPresets } from './ColorPresets';
import { ExportPreview } from './ExportPreview';
import { EXPORT_SECTIONS, scopeReportsData } from './exportSections';
import { resolveSections, loadImageAsDataUrl, buildMeta } from './exportUtils';
import { generateCSV } from './csvExport';
import { generatePDF } from './pdfExport';
import { generatePPTX } from './pptxExport';
import {
  DEFAULT_EXPORT_CONFIG,
  PAGE_DEFAULTS,
  loadExportConfig,
  saveExportConfig,
} from './exportDefaults';

const FORMAT_OPTIONS = [
  { value: 'csv', label: 'CSV', icon: faFileCsv },
  { value: 'pdf', label: 'PDF', icon: faFilePdf },
  { value: 'pptx', label: 'PowerPoint', icon: faFilePowerpoint },
];

const ENTITY_ID_PARAMS = { campaign: 'campaignId', zone: 'zoneId', ad: 'adId' };

export const ExportBuilderModal = ({
  isOpen,
  onClose,
  pageType = 'reports',
  data = {},
  dateRange = 'today',
  filters = {},
}) => {
  const [config, setConfig] = useState(loadExportConfig());
  const [isExporting, setIsExporting] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoRemoving, setLogoRemoving] = useState(false);
  const fileInputRef = useRef(null);
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const descColor = useColorModeValue('gray.500', 'gray.500');

  // Load config + current report logo on open
  useEffect(() => {
    if (!isOpen) return undefined;
    setConfig(loadExportConfig());
    let cancelled = false;
    apiClient
      .get('/settings/report-logo')
      .then((res) => {
        if (!cancelled) setLogoUrl(res.url || null);
      })
      .catch((err) => {
        if (!cancelled) console.error('Failed to load report logo:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Auto-save config when it changes
  useEffect(() => {
    if (isOpen) {
      saveExportConfig(config);
    }
  }, [config, isOpen]);

  const updateConfig = (path, value) => {
    setConfig((prev) => {
      const newConfig = { ...prev };
      const keys = path.split('.');
      let current = newConfig;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newConfig;
    });
  };

  const sectionDefs = useMemo(
    () => (EXPORT_SECTIONS[pageType] || []).filter((s) => s.available(data)),
    [pageType, data]
  );

  const isSectionSelected = (def) => {
    const chosen = config.sections?.[pageType]?.[def.key];
    return chosen === undefined ? def.default : !!chosen;
  };

  const selectedDefs = sectionDefs.filter(isSectionSelected);

  const handleSelectAllSections = () => {
    updateConfig(
      `sections.${pageType}`,
      Object.fromEntries(sectionDefs.map((s) => [s.key, true]))
    );
  };

  const handleDefaultSections = () => {
    updateConfig(`sections.${pageType}`, {});
  };

  const handleReset = () => {
    setConfig(DEFAULT_EXPORT_CONFIG);
    saveExportConfig(DEFAULT_EXPORT_CONFIG);
    toast({ title: 'Settings reset to defaults', status: 'info', duration: 2000 });
  };

  const handleLogoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const res = await apiClient.upload('/settings/report-logo', formData);
      setLogoUrl(res.url || null);
    } catch (err) {
      console.error('Logo upload failed:', err);
      toast({ title: 'Logo upload failed', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoRemove = async () => {
    setLogoRemoving(true);
    try {
      await apiClient.delete('/settings/report-logo');
      setLogoUrl(null);
    } catch (err) {
      console.error('Logo remove failed:', err);
      toast({ title: 'Failed to remove logo', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setLogoRemoving(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      let mergedData = { ...data };
      const entity = pageType === 'reports' ? filters.entity || null : null;

      if (pageType === 'reports') {
        const dates = parseDateRange(dateRange);
        const params = {
          start: dates.start.toISOString(),
          end: dates.end.toISOString(),
        };
        if (filters.site && filters.site !== 'all') params.site = filters.site;
        if (filters.type && filters.type !== 'all') params.type = filters.type;
        if (filters.filterBots) params.filterBots = 'true';
        // daily-trend and audience accept any entity id; the per-entity daily
        // endpoints only accept their own id (see server/src/routes/analytics.js)
        const entityParams = entity ? { [ENTITY_ID_PARAMS[entity.type]]: entity.id } : {};

        const needs = new Set(selectedDefs.map((s) => s.needs).filter(Boolean));
        const tasks = [];
        if (config.granularity !== 'all') {
          const periodParams = { ...params, granularity: config.granularity };
          if (needs.has('trend')) {
            tasks.push(analyticsApi.getDailyTrend({ ...periodParams, ...entityParams }).then((res) => {
              mergedData.trend = res.data;
            }));
          }
          if (needs.has('adPeriods')) {
            const adParams = entity?.type === 'ad' ? { ...periodParams, adId: entity.id } : periodParams;
            tasks.push(analyticsApi.getAdDaily(adParams).then((res) => {
              mergedData.adPeriods = res.data;
            }));
          }
          if (needs.has('campaignPeriods')) {
            const campaignParams = entity?.type === 'campaign' ? { ...periodParams, campaignId: entity.id } : periodParams;
            tasks.push(analyticsApi.getCampaignDaily(campaignParams).then((res) => {
              mergedData.campaignPeriods = res.data;
            }));
          }
          if (needs.has('zonePeriods')) {
            const zoneParams = entity?.type === 'zone' ? { ...periodParams, zoneId: entity.id } : periodParams;
            tasks.push(analyticsApi.getZoneDaily(zoneParams).then((res) => {
              mergedData.zonePeriods = res.data;
            }));
          }
        }
        if (needs.has('audience')) {
          tasks.push(analyticsApi.getReportsAudience({ ...params, ...entityParams }).then((res) => {
            mergedData.audience = res;
          }));
        }
        await Promise.all(tasks);
        if (entity) {
          mergedData = scopeReportsData(mergedData, entity);
        }
      }

      const logo = logoUrl ? await loadImageAsDataUrl(logoUrl) : null;
      const sections = resolveSections(pageType, config, mergedData);
      const meta = buildMeta({
        config,
        pageType,
        dateRange,
        granularity: pageType === 'reports' ? config.granularity : null,
        logo,
        entity,
      });

      if (config.exportType === 'csv') {
        generateCSV({ sections, meta });
      } else if (config.exportType === 'pptx') {
        await generatePPTX({ sections, meta });
      } else {
        await generatePDF({ sections, meta });
      }

      toast({ title: 'Export ready', status: 'success', duration: 3000 });
      onClose();
    } catch (err) {
      console.error('Export failed:', err);
      toast({ title: 'Export failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setIsExporting(false);
    }
  };

  const pageDefaults = PAGE_DEFAULTS[pageType] || PAGE_DEFAULTS.reports;
  const activeFormat = FORMAT_OPTIONS.find((f) => f.value === config.exportType) || FORMAT_OPTIONS[1];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent bg={bgColor} borderRadius="2xl" mx={4} maxH="90vh" overflow="hidden">
        <ModalHeader borderBottom="1px solid" borderColor={borderColor}>
          Export Report
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={6} overflow="auto">
          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
            {/* Left Column - Controls */}
            <GridItem>
              <VStack spacing={5} align="stretch">
                {/* Format Selection */}
                <Box>
                  <Text fontSize="sm" fontWeight="500" color={labelColor} mb={2}>
                    Format
                  </Text>
                  <ButtonGroup size="sm" isAttached variant="outline" w="100%">
                    {FORMAT_OPTIONS.map((format) => (
                      <Button
                        key={format.value}
                        flex={1}
                        leftIcon={<FontAwesomeIcon icon={format.icon} />}
                        colorScheme={config.exportType === format.value ? 'brand' : 'gray'}
                        variant={config.exportType === format.value ? 'solid' : 'outline'}
                        onClick={() => updateConfig('exportType', format.value)}
                        borderRadius="xl"
                      >
                        {format.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Box>

                <Divider />

                {/* Sections */}
                <Box>
                  <HStack mb={3}>
                    <Text fontSize="sm" fontWeight="600" color={labelColor} textTransform="uppercase" letterSpacing="wide">
                      Sections
                    </Text>
                    <Spacer />
                    <Button size="xs" variant="ghost" onClick={handleSelectAllSections}>
                      Select all
                    </Button>
                    <Button size="xs" variant="ghost" onClick={handleDefaultSections}>
                      Defaults
                    </Button>
                  </HStack>
                  <VStack spacing={2} align="stretch">
                    {sectionDefs.map((def) => (
                      <Checkbox
                        key={def.key}
                        size="sm"
                        colorScheme="brand"
                        isChecked={isSectionSelected(def)}
                        onChange={(e) => updateConfig(`sections.${pageType}.${def.key}`, e.target.checked)}
                        alignItems="flex-start"
                        sx={{ '.chakra-checkbox__control': { mt: 1 } }}
                      >
                        <Text fontSize="sm">{def.label}</Text>
                        <Text fontSize="xs" color={descColor}>
                          {def.description}
                        </Text>
                      </Checkbox>
                    ))}
                  </VStack>
                </Box>

                {/* Granularity (reports only) */}
                {pageType === 'reports' && (
                  <>
                    <Divider />
                    <FormControl>
                      <FormLabel fontSize="sm" color={labelColor}>Granularity</FormLabel>
                      <Select
                        size="sm"
                        borderRadius="lg"
                        value={config.granularity}
                        onChange={(e) => updateConfig('granularity', e.target.value)}
                      >
                        <option value="day">Daily</option>
                        <option value="week">Weekly</option>
                        <option value="month">Monthly</option>
                        <option value="all">Full Run</option>
                      </Select>
                    </FormControl>
                  </>
                )}

                <Divider />

                {/* Colors Section */}
                <Box>
                  <Text fontSize="sm" fontWeight="600" color={labelColor} mb={3} textTransform="uppercase" letterSpacing="wide">
                    Colors
                  </Text>
                  <VStack spacing={4} align="stretch">
                    <ColorPresets
                      label="Primary Color"
                      type="primary"
                      selectedColor={config.colors.primary}
                      onSelect={(color) => updateConfig('colors.primary', color)}
                    />
                    <ColorPresets
                      label="Accent Color"
                      type="accent"
                      selectedColor={config.colors.accent}
                      onSelect={(color) => updateConfig('colors.accent', color)}
                    />
                  </VStack>
                </Box>

                <Divider />

                {/* Branding Section */}
                <Box>
                  <Text fontSize="sm" fontWeight="600" color={labelColor} mb={3} textTransform="uppercase" letterSpacing="wide">
                    Branding
                  </Text>
                  <VStack spacing={3} align="stretch">
                    <FormControl>
                      <FormLabel fontSize="sm" color={labelColor}>Company Name</FormLabel>
                      <Input
                        size="sm"
                        borderRadius="lg"
                        value={config.branding.companyName}
                        onChange={(e) => updateConfig('branding.companyName', e.target.value)}
                        placeholder="StatFalcon"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color={labelColor}>Logo</FormLabel>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        style={{ display: 'none' }}
                        onChange={handleLogoFile}
                      />
                      <HStack spacing={3}>
                        {logoUrl && (
                          <Box
                            border="1px solid"
                            borderColor={borderColor}
                            borderRadius="lg"
                            p={1}
                            bg="white"
                          >
                            <Image src={logoUrl} alt="Report logo" h="32px" maxW="96px" objectFit="contain" />
                          </Box>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          borderRadius="lg"
                          leftIcon={<FontAwesomeIcon icon={faUpload} />}
                          isLoading={logoUploading}
                          loadingText="Uploading..."
                          onClick={() => fileInputRef.current?.click()}
                        >
                          {logoUrl ? 'Replace' : 'Upload Logo'}
                        </Button>
                        {logoUrl && (
                          <Button
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            borderRadius="lg"
                            leftIcon={<FontAwesomeIcon icon={faTrash} />}
                            isLoading={logoRemoving}
                            onClick={handleLogoRemove}
                          >
                            Remove
                          </Button>
                        )}
                      </HStack>
                    </FormControl>
                  </VStack>
                </Box>

                <Divider />

                {/* Text Section */}
                <Box>
                  <Text fontSize="sm" fontWeight="600" color={labelColor} mb={3} textTransform="uppercase" letterSpacing="wide">
                    Text
                  </Text>
                  <VStack spacing={3} align="stretch">
                    <FormControl>
                      <FormLabel fontSize="sm" color={labelColor}>Title</FormLabel>
                      <Input
                        size="sm"
                        borderRadius="lg"
                        value={config.text.title}
                        onChange={(e) => updateConfig('text.title', e.target.value)}
                        placeholder={pageDefaults.title}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color={labelColor}>Subtitle</FormLabel>
                      <Input
                        size="sm"
                        borderRadius="lg"
                        value={config.text.subtitle}
                        onChange={(e) => updateConfig('text.subtitle', e.target.value)}
                        placeholder={pageDefaults.subtitle}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" color={labelColor}>Footer Text</FormLabel>
                      <Input
                        size="sm"
                        borderRadius="lg"
                        value={config.text.footerText}
                        onChange={(e) => updateConfig('text.footerText', e.target.value)}
                        placeholder={`Generated by ${config.branding.companyName || 'StatFalcon'}`}
                      />
                    </FormControl>
                  </VStack>
                </Box>
              </VStack>
            </GridItem>

            {/* Right Column - Preview */}
            <GridItem>
              <ExportPreview
                config={config}
                pageType={pageType}
                logoUrl={logoUrl}
                selectedSections={selectedDefs.map((s) => s.label)}
              />
            </GridItem>
          </Grid>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor={borderColor} gap={3}>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<FontAwesomeIcon icon={faRotateLeft} />}
            onClick={handleReset}
            borderRadius="xl"
          >
            Reset Defaults
          </Button>
          <HStack spacing={3} ml="auto">
            <Button variant="ghost" onClick={onClose} borderRadius="xl">
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleExport}
              isLoading={isExporting}
              loadingText="Exporting..."
              borderRadius="xl"
              isDisabled={selectedDefs.length === 0}
              leftIcon={<FontAwesomeIcon icon={activeFormat.icon} />}
            >
              Export {activeFormat.label}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
